/**
 * 进详情前可选写入来源；router 进入 MarketDetail / CryptoUpdownDetail / SportLiveMatchDetail 时统一打 market.open。
 * 同详情内 silent replace（无 pending）不重复打点。
 */

/** @type {null | { source?: string, slug?: string, kind?: string, marketId?: string }} */
let pendingMarketOpen = null;

/**
 * @param {{ source?: string, slug?: string, kind?: string, marketId?: string }} meta
 */
export function setMarketOpenMeta(meta = {}) {
  const source = String(meta?.source ?? '').trim();
  const slug = String(meta?.slug ?? '').trim();
  const kind = String(meta?.kind ?? '').trim();
  const marketId = String(meta?.marketId ?? '').trim();
  pendingMarketOpen = {
    source: source || undefined,
    slug: slug || undefined,
    kind: kind === 'updown' || kind === 'sport' || kind === 'market' ? kind : undefined,
    marketId: marketId || undefined,
  };
}

/**
 * @param {import('vue-router').RouteLocationRaw | { name?: string, params?: Record<string, unknown> }} to
 * @param {string} [source]
 */
export function applyMarketOpenMetaFromLocation(to, source = '') {
  const name = typeof to?.name === 'string' ? to.name : '';
  if (name !== 'MarketDetail' && name !== 'CryptoUpdownDetail' && name !== 'SportLiveMatchDetail') {
    return;
  }
  setMarketOpenMeta({
    marketId: String(to?.params?.id ?? to?.params?.matchId ?? '').trim() || undefined,
    kind:
      name === 'CryptoUpdownDetail'
        ? 'updown'
        : name === 'SportLiveMatchDetail'
        ? 'sport'
        : 'market',
    source,
  });
}

/** @returns {null | { source?: string, slug?: string, kind?: string, marketId?: string }} */
export function consumeMarketOpenMeta() {
  const next = pendingMarketOpen;
  pendingMarketOpen = null;
  return next;
}

/**
 * @param {{ name?: unknown }} [from]
 * @returns {string}
 */
export function inferMarketOpenSource(from) {
  const name = typeof from?.name === 'string' ? from.name : '';
  if (!name) return 'direct';
  if (
    name === 'Home' ||
    name === 'Explore' ||
    name === 'Watchlist' ||
    name === 'Crypto' ||
    name === 'Sport'
  ) {
    return 'feed';
  }
  if (name === 'Search') return 'search';
  if (name === 'Notifications' || name === 'Notification') return 'notification';
  if (name === 'Account') return 'account';
  if (name === 'Profile' || name === 'UserProfile') return 'profile';
  if (name === 'Leagues') return 'leagues';
  if (name === 'HotTopicDetail' || name === 'BreakingNewsDetail') return 'topic';
  return name.slice(0, 48) || 'direct';
}

/**
 * @param {{ name?: unknown, params?: Record<string, unknown> }} to
 * @param {{ name?: unknown, params?: Record<string, unknown> }} from
 * @returns {null | Record<string, unknown>}
 */
export function resolveMarketOpenTrackPayload(to, from) {
  const toName = typeof to?.name === 'string' ? to.name : '';
  if (
    toName !== 'MarketDetail' &&
    toName !== 'CryptoUpdownDetail' &&
    toName !== 'SportLiveMatchDetail'
  ) {
    return null;
  }

  const pending = consumeMarketOpenMeta();
  const fromName = typeof from?.name === 'string' ? from.name : '';
  const toId = String(to?.params?.id ?? to?.params?.matchId ?? '').trim();
  const fromId = String(from?.params?.id ?? from?.params?.matchId ?? '').trim();

  // 同详情内切盘 / query 变化：无显式 meta 则不记「点击盘口」
  if (!pending && fromName === toName) return null;

  const marketId = (pending?.marketId || toId).trim();
  if (!marketId) return null;

  const kind =
    pending?.kind ||
    (toName === 'CryptoUpdownDetail'
      ? 'updown'
      : toName === 'SportLiveMatchDetail'
      ? 'sport'
      : 'market');

  return {
    marketId,
    slug: pending?.slug || undefined,
    kind,
    source: pending?.source || inferMarketOpenSource(from) || 'direct',
    fromRoute: fromName || undefined,
    // 同名详情但 id 变化且带了 meta 时仍可区分
    replaced: Boolean(fromName === toName && fromId && fromId !== marketId) || undefined,
  };
}
